<script lang="ts" module>
  import { tv } from 'tailwind-variants';

  import Icon, { type IconName } from '@/ui/base/icon/Icon.svelte';

  import MarkerPill, { type MarkerPillSubDim } from '../../buttons/MarkerPill.svelte';
  import MarkerPillPerspective from '../../buttons/MarkerPillPerspective.svelte';
  import ExpandableCard, { type ExpandableCardProps } from '../../card/ExpandableCard.svelte';

  export interface DimensionCardProps {
    pills: (MarkerPillData | MarkerPillPerspectiveData)[];
    textProperty?: string;
    onTextPropertyChange?: (textProperty: string | undefined) => void;
    label: string;
    score?: number;
    todoIndicator?: boolean;
    disabled?: boolean;
    open?: boolean;
    useAccordion?: ExpandableCardProps['useAccordion'];
    class?: string;
    useableSubdims?: MarkerPillSubDim[];
  }

  export type MarkerPillType = 'MarkerPillPerspectiveData' | 'MarkerPillData';

  export interface MakerPillItem {
    id: string;
    key: string;
    type: MarkerPillType;
    selected?: boolean;
  }

  export interface MarkerPillData extends MakerPillItem {
    label?: string;
    value?: number;
  }

  export interface MarkerPillPerspectiveData extends MakerPillItem {
    labelSender?: string;
    labelReceiver?: string;
    valueSender?: number;
    valueReceiver?: number;
  }
</script>

<script lang="ts">
  let {
    open: openCard = false,
    textProperty: textProp,
    score: scoreProp = undefined,
    useableSubdims: useableSubdimsProp = ['subdim1', 'subdim2', 'subdim3'],
    ...props
  }: DimensionCardProps = $props();

  let lastOpenCard = false;
  let disabledProp = $derived(props.useAccordion?.item.disabled || props.disabled);

  let icon = $derived.by(() => {
    let iconName = 'Middle';
    if (scoreProp != undefined) {
      const score: number = scoreProp ?? 0;
      iconName =
        disabledProp ? 'Neutral'
        : score < 50 ? 'Bad'
        : score < 100 ? 'Middle'
        : 'Good';
    }
    return ('Tacho' + iconName) as IconName;
  });

  let contentData = $derived.by(() => {
    return props.pills.map((pill) => ({
      ...pill,
      selected: pill.key === textProp,
    }));
  });

  // Select a pill
  function onClickMarkerPill(prop: string | undefined) {
    textProp = textProp !== prop ? prop : undefined;
    props.onTextPropertyChange?.(textProp);
  }

  // If card was open by accordion, saved state to prevent effect loop
  $effect(() => {
    if (openCard != lastOpenCard) {
      lastOpenCard = openCard;
      if (!openCard && textProp) onClickMarkerPill(undefined);
    }
  });

  // Styles for button text
  const textStyle = tv({
    base: 'grow text-left text-label-sm font-medium',
    variants: {
      disabled: {
        true: 'text-c-disabled',
        false: 'text-c-base',
      },
    },
  });
</script>

<ExpandableCard bind:expanded={openCard} useAccordion={props.useAccordion}>
  {#snippet header()}
    {#if props.todoIndicator}
      <Icon icon="TodoIndicator" class="size-5 flex-none"></Icon>
    {/if}
    <p class={textStyle({ disabled: disabledProp })}>
      {props.label}
    </p>
    <div class="size-8">
      {#if scoreProp != undefined}
        <Icon {icon} class="size-8 flex-none"></Icon>
      {/if}
    </div>
  {/snippet}
  {#snippet content()}
    <hr class="border-t-c-divider" />
    {#each contentData as data, i (data.id)}
      {#if data.type === 'MarkerPillData'}
        <MarkerPill
          label={(data as MarkerPillData).label ?? ''}
          variant={useableSubdimsProp[i % useableSubdimsProp.length] ?? 'subdim1'}
          value={(data as MarkerPillData).value ?? 0}
          class="w-fit"
          selected={data.selected}
          onclick={() => {
            onClickMarkerPill(data.key);
          }}
        ></MarkerPill>
      {:else if data.type === 'MarkerPillPerspectiveData'}
        <MarkerPillPerspective
          labelSender={(data as MarkerPillPerspectiveData).labelSender ?? ''}
          labelReceiver={(data as MarkerPillPerspectiveData).labelReceiver ?? ''}
          valueSender={(data as MarkerPillPerspectiveData).valueSender ?? 0}
          valueReceiver={(data as MarkerPillPerspectiveData).valueReceiver ?? 0}
          onClickSender={() => {
            onClickMarkerPill(data.key);
          }}
          onClickReceiver={() => {
            onClickMarkerPill(data.key);
          }}
          selected={data.selected}
        ></MarkerPillPerspective>
      {/if}
    {/each}
  {/snippet}
</ExpandableCard>
